// Standalone V60 recipe parsing + copy rendering. Plain browser global, no
// build step, no dependencies. Combines what was previously split across
// recipe-v60-debug.js (parsing) and recipe-v60-render.js (copy) into one
// file, so the debug-only files can be removed.
//
// Exposes:
//   window.parseRecipeV60(raw)                                  -> parsed object
//   window.renderRecipeV60Schema(parsed, productTitle, recipeImages)       -> Recipe JSON-LD
//   window.renderRecipeV60Html(parsed, tastingHook, process, country) -> { specs, method }
//
(() => {
  // ---------------------------------------------------------------------
  // Parsing — unchanged from recipe-v60-debug.js
  // ---------------------------------------------------------------------

  const FALLBACKS = {
    dose: "18g",
    brew_water: "300g",
    temp: "92'c",
    time: "3:00 min",
    pour: "55g, 120g, 180g, 240g, 300g",
  };
  const WEIGHT_RE = /(\d+(?:\.\d+)?)\s*g\b/gi;
  const INTERVAL_RE = /(\d+)\s*sec/i;

  function normalizeQuotes(value) {
    // Curly single quotes (\u2018 \u2019 \u02bc) -> straight apostrophe.
    return String(value).replace(/[\u2018\u2019\u02BC]/g, "'");
  }

  function toNumber(value) {
    // "18g" -> 18, "300g" -> 300, "18.5g" -> 18.5
    return parseFloat(String(value).replace(/[^\d.]/g, "")) || 0;
  }

  function parseRecipeV60(raw) {
    let lines;
    if (raw == null) {
      lines = [];
    } else if (Array.isArray(raw)) {
      lines = raw;
    } else if (typeof raw === "string") {
      lines = JSON.parse(raw);
    } else {
      throw new TypeError(
        "recipe_v60 must be a JSON array string or an array of strings",
      );
    }
    if (!Array.isArray(lines)) {
      throw new TypeError("recipe_v60 parsed value must be an array");
    }
    const fields = {};
    const weightFragments = [];
    for (const line of lines) {
      if (typeof line !== "string") continue;
      const text = normalizeQuotes(line.trim());
      if (!text) continue;
      const colon = text.indexOf(":");
      if (colon === -1) {
        // Keyless line — weight-only fragment, e.g. "55g-120g-180g-240g-300g".
        weightFragments.push(text);
        continue;
      }
      const key = text.slice(0, colon).trim().toLowerCase();
      const value = text.slice(colon + 1).trim();
      fields[key] = value;
    }
    const dose = fields.dose ?? FALLBACKS.dose;
    const brew_water =
      fields["brew water"] ?? fields.water ?? FALLBACKS.brew_water;
    const temp = fields.temp ?? FALLBACKS.temp;
    const time = fields.time ?? FALLBACKS.time;
    const pour = fields.pour ?? FALLBACKS.pour;
    // Pour weights can live inline in the Pour string OR on trailing keyless
    // lines. Extract from both, never from Dose/Brew Water (they carry "g" too).
    const weightSource = [pour, ...weightFragments].join(" ");
    const cumulative = Array.from(weightSource.matchAll(WEIGHT_RE), (m) =>
      Number(m[1]),
    );
    const pour_count = cumulative.length;
    const step_water = cumulative.map((c, i) => c - (cumulative[i - 1] ?? 0));
    const baseMatch = pour.match(INTERVAL_RE);
    const base_interval = baseMatch ? Number(baseMatch[1]) : 30;
    const interval = cumulative.map((_, i) => base_interval * (i + 1));
    const doseNum = toNumber(dose);
    const waterNum = toNumber(brew_water);
    // Ratio rounded to 2dp (coffee:water ~1:16.67). Unround if more
    // precision is ever needed downstream.
    const ratio =
      doseNum > 0 ? Math.round((waterNum / doseNum) * 100) / 100 : 0;
    return {
      dose,
      brew_water,
      temp,
      time,
      pour_count,
      step_water,
      cumulative,
      interval,
      ratio,
    };
  }

  // ---------------------------------------------------------------------
  // Rendering
  //
  // Every formatting decision here was ambiguous in the original hand-written
  // copy template and has been resolved against real product data. Do not
  // re-derive these against the template's literal {{...}} notation:
  //
  //   - dose/brew_water/temp/time already include their unit in the parsed
  //     string ("18g", "92'c") — interpolate directly, never strip/re-add.
  //   - step_water/cumulative are bare numbers — "g" is appended here as
  //     literal text; interval renders as m:ss via formatTime().
  //   - the pour timeline is cumulative[i] at interval[i - 1] for each pour
  //     after the bloom (the final row is interval[pour_count - 2]; the
  //     index after it would be a pour that doesn't exist).
  //   - repeat_list is empty for pour_count === 3 (the common case) — no
  //     "repeat" language for a single occurrence.
  //   - final_pour_technique's <120g branch includes exactly 120g.
  // ---------------------------------------------------------------------

  function formatTime(totalSeconds) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return minutes + ":" + String(seconds).padStart(2, "0");
  }

  function formatBrewTime(timeStr) {
    // "3:00 min" -> "2:45 min" (total minus 15s drawdown); caller adds ±15s.
    const match = String(timeStr).match(/(\d+):(\d{2})/);
    if (!match) return String(timeStr);
    const totalSeconds = Number(match[1]) * 60 + Number(match[2]) - 15;
    return formatTime(totalSeconds) + " min";
  }

  function buildPourScheduleRows(parsed) {
    const { cumulative, interval } = parsed;
    return cumulative.map((total, i) => ({
      label: i === 0 ? "Bloom" : formatTime(interval[i - 1]),
      value: total + "g",
    }));
  }

  function ordinal(n) {
    const words = [
      "zeroth",
      "first",
      "second",
      "third",
      "fourth",
      "fifth",
      "sixth",
      "seventh",
      "eighth",
      "ninth",
      "tenth",
      "eleventh",
      "twelfth",
      "thirteenth",
      "fourteenth",
      "fifteenth",
      "sixteenth",
      "seventeenth",
      "eighteenth",
      "nineteenth",
      "twentieth",
    ];
    // ponytail: covers pours 1-20; V60 recipes cap at ~6, fallback is rare.
    return words[n] ?? n + "th";
  }

  function joinList(items) {
    if (items.length <= 1) return items.join("");
    return items.slice(0, -1).join(", ") + " and " + items[items.length - 1];
  }

  function buildRepeatList(parsed) {
    const { pour_count, step_water, interval, cumulative } = parsed;
    if (pour_count <= 3) return "";
    const fragments = [];
    for (let j = 2; j <= pour_count - 2; j++) {
      fragments.push(
        "at " + formatTime(interval[j - 1]) + " with " + step_water[j] + "g",
      );
    }
    const joined = joinList(fragments);
    return (
      "\u000A\u000ARepeat " +
      joined +
      " until your total reaches " +
      cumulative[pour_count - 2] +
      "g."
    );
  }

  function finalPourTechnique(finalStepWater) {
    return finalStepWater >= 120
      ? "Pour the first half in slow circles, then finish the second half straight down the centre with no circular motion."
      : "Pour in slow, steady circles.";
  }

  function buildMethodSteps(parsed) {
    const { pour_count, dose, temp, step_water, interval, cumulative, time } =
      parsed;
    const steps = [];

    steps.push({
      title: "Preparation",
      body:
        "Grind " +
        dose +
        " of coffee to a medium-fine consistency, a little coarser than table salt. " +
        "Heat your filtered water to " +
        temp +
        " and rinse your paper filter when it\u2019s up to temperature. " +
        "\u000A\u000AAdd your coffee to the cone, and shake gently to level the coffee bed.",
    });

    steps.push({
      title: "Bloom the coffee",
      body:
        "Pour " +
        step_water[0] +
        "g of water over the grounds in a slow spiral, making sure all the coffee is saturated. " +
        "Leave the bed undisturbed and enjoy the aroma until " +
        interval[0] +
        " seconds have passed.",
    });

    if (pour_count >= 3) {
      const repeatList = buildRepeatList(parsed);
      const middleOrdinals = [];
      for (let i = 2; i <= pour_count - 1; i++) {
        middleOrdinals.push(ordinal(i));
      }
      steps.push({
        title:
          "The " +
          joinList(middleOrdinals) +
          " pour" +
          (middleOrdinals.length > 1 ? "s" : ""),
        body:
          "At " +
          formatTime(interval[0]) +
          ", add " +
          step_water[1] +
          "g of water in slow concentric circles, " +
          "moving from the edge of the bed inwards, bringing your total to " +
          cumulative[1] +
          "g." +
          (repeatList ? " " + repeatList : ""),
      });
    }

    const finalIndex = pour_count - 1;
    steps.push({
      title: "The " + ordinal(pour_count) + " and final pour",
      body:
        "At " +
        formatTime(interval[finalIndex - 1]) +
        ", pour the remaining " +
        step_water[finalIndex] +
        "g. " +
        finalPourTechnique(step_water[finalIndex]) +
        " " +
        "Allow the bed to sit while it draws down fully, you\u2019re aiming for a total extraction time of " +
        time +
        "[[SUP]].",
    });

    steps.push({
      title: "Stir and enjoy",
      body:
        "Stir gently to mix, and enjoy." +
        "\u000A\u000ASip slowly, and savour the coffee as it cools down.",
    });

    return steps;
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
      .replace(/\n/g, "<br>");
  }

  /**
   * @param {object} parsed - output of parseRecipeV60()
   * @param {string} tastingHook - e.g. "notes of peach, raspberry and honey"
   * @param {string} process - coffee process, e.g. "Anaerobic Natural"
   * @param {string} country - coffee origin country, e.g. "El Salvador"
   * @returns {{specs: string, method: string}} HTML recipe markup split into
   *   the spec column (intro + quick reference + pour schedule) and the method
   *   column (numbered steps). Dynamic values are HTML-escaped. Yield (ml) and
   *   cup count are derived from parsed.brew_water (1 cup = 250ml, min 1).
   */
  function renderRecipeV60Html(parsed, tastingHook, process, country) {
    const yieldMl = toNumber(parsed.brew_water);
    const cupCount = Math.max(1, Math.round(yieldMl / 250));
    const originClause =
      process && country
        ? "from this " +
          escapeHtml(process) +
          " " +
          escapeHtml(country) +
          " coffee"
        : "in the cup";
    const brewTime = formatBrewTime(parsed.time);
    const quickReferenceRows = [
      ["Dose", escapeHtml(parsed.dose)],
      ["Water", escapeHtml(parsed.brew_water)],
      ["Temp", escapeHtml(parsed.temp)],
      ["Brew Time", escapeHtml(brewTime) + " <sup>\u00b115s</sup>"],
    ]
      .map(
        (row) =>
          "<div><span>" + row[0] + "</span><span>" + row[1] + "</span></div>",
      )
      .join("");

    const pourRows = buildPourScheduleRows(parsed)
      .map(
        (row) =>
          "<div><span>" +
          row.label +
          "</span><span>" +
          row.value +
          "</span></div>",
      )
      .join('<div class="recipe-v60__pour-separator"></div>');

    const methodSteps = buildMethodSteps(parsed)
      .map(
        (step, i) =>
          "<div><strong>Step " +
          (i + 1) +
          " - " +
          escapeHtml(step.title) +
          "</strong><br>" +
          escapeHtml(step.body).replace("[[SUP]]", " <sup>\u00b115s</sup>") +
          "</div>",
      )
      .join("");

    return {
      specs:
        "<p>A clean pour that draws out " +
        escapeHtml(tastingHook) +
        " " +
        originClause +
        ". Slow down, let the water do the work.</p>" +
        "<p><strong>Tools:</strong> You\u2019ll need a V60 dripper, paper filter, digital scale, gooseneck kettle and a burr grinder.</p>" +
        "<p><strong>Servings:</strong> Makes " +
        escapeHtml(yieldMl) +
        "ml, about " +
        cupCount +
        (cupCount === 1 ? " cup" : " cups") +
        ".</p>" +
        '<div class="recipe-v60__reference">' +
        "<h3>Quick reference</h3>" +
        "<hr>" +
        '<div class="recipe-v60__specs">' +
        quickReferenceRows +
        "</div>" +
        "<hr>" +
        "<h4>Pour Schedule</h4>" +
        '<div class="recipe-v60__pours">' +
        pourRows +
        "</div>" +
        "</div>",
      method: "<h3>Method</h3>" + methodSteps,
    };
  }

  // ---------------------------------------------------------------------
  // Recipe JSON-LD (schema.org/Recipe). Reuses buildMethodSteps so the
  // schema step text is byte-identical to the visible method copy.
  // ---------------------------------------------------------------------

  function formatIsoDuration(timeStr) {
    const value = String(timeStr).toLowerCase();
    const colon = value.match(/(\d+):(\d{2})/);
    if (colon) {
      const minutes = Number(colon[1]);
      const seconds = Number(colon[2]);
      return seconds
        ? "PT" + minutes + "M" + seconds + "S"
        : "PT" + minutes + "M";
    }
    const sec = value.match(/(\d+)\s*sec/);
    if (sec) return "PT" + sec[1] + "S";
    return "PT0S";
  }

  // Bare quantity for schema.org recipeYield ("1 cup"). The Servings line in
  // renderRecipeV60Html keeps the full sentence ("Makes 300ml, about 1 cup.").
  function buildYieldQuantity(parsed) {
    const cupCount = Math.max(1, Math.round(toNumber(parsed.brew_water) / 250));
    return cupCount + (cupCount === 1 ? " cup" : " cups");
  }
  function buildServingsText(parsed) {
    return (
      "Makes " + toNumber(parsed.brew_water) + "ml, about " + buildYieldQuantity(parsed) + "."
    );
  }

  /**
   * @param {object} parsed - output of parseRecipeV60()
   * @param {string} productTitle - e.g. "Ethiopia Rachia"
   * @param {object[]} recipeImages - recipe_generation_images metaobject
   *   entries ({brew_method, image_1_1, image_4_3, image_16_9}), one per brew
   *   method
   * @returns {object} schema.org/Recipe JSON-LD
   */
  function renderRecipeSchema(parsed, productTitle, recipeImages) {
    const category = "Pour Over";
    // Method-level finished-drink image. A bag render fails Google's "image
    // of the completed dish" requirement, so omit image when no entry matches
    // rather than falling back to the product image.
    const entry = (recipeImages || []).find((e) => e.brew_method === category);
    const images = entry
      ? [entry.image_1_1, entry.image_4_3, entry.image_16_9].filter(Boolean)
      : [];
    const image = images.length ? images : null;
    const steps = buildMethodSteps(parsed).map((step) => {
      const instruction = {
        "@type": "HowToStep",
        name: step.title,
        // [[SUP]] marks the ±15s brew-time note; render the same visible form
        // renderRecipeV60Html produces (<sup>±15s</sup>).
        text: step.body.replace("[[SUP]]", " \u00b115s"),
      };
      if (image) instruction.image = image;
      return instruction;
    });
    const schema = {
      "@context": "https://schema.org",
      "@type": "Recipe",
      name: "V60 Pour Over \u2014 " + productTitle,
      totalTime: formatIsoDuration(parsed.time),
      recipeYield: buildYieldQuantity(parsed),
      // ponytail: grind descriptor mirrors the Preparation step copy; update
      // alongside buildMethodSteps if the grind language ever changes.
      recipeIngredient: [
        parsed.dose + " coffee, ground medium-fine",
        parsed.brew_water + " filtered water",
      ],
      recipeCategory: category,
      author: { "@type": "Organization", "name": "Deluca Specialty Coffees" },
      recipeInstructions: steps,
    };
    if (image) schema.image = image;
    return schema;
  }
  window.parseRecipeV60 = parseRecipeV60;
  window.renderRecipeV60Html = renderRecipeV60Html;
  window.renderRecipeV60Schema = renderRecipeSchema;
  // Exposed individually in case the JSON-LD step needs to reuse these
  // pieces without re-deriving the logic a second time.
  window.recipeV60RenderHelpers = {
    buildPourScheduleRows,
    buildRepeatList,
    finalPourTechnique,
    buildMethodSteps,
  };
})();
